import React, { useEffect, useState } from 'react';
import { DebounceInput } from 'react-debounce-input';
import classNames from 'classnames';
import { azElErrorMessage } from 'common/utils/constants';
import { isElValid, isAzValid } from 'common/utils/helpers';
import { IconLoader } from 'common/IconLoader';
import SvgAF from 'components/shared/SvgAF/SvgAF';
import CommissionErrorBox from 'components/features/Commissioning/CommissionErrorBox/CommissionErrorBox';
import './BeamRankListItem.css';
import { useBeamOperations } from '../../../../hooks/useBeamOperations';
const TIMEOUT = process.env.NODE_ENV === 'test' ? 10 : 1000;

const BeamRankListItem = (props) => {
  const [errorState, setErrorState] = useState(null);
  const [kpiMetrics, setKpiMetrics] = useState(null);
  const {
    setCustomBeam,
    cancelCustomBeam,
    isLoading,
    error,
    clearError
  } = useBeamOperations(props.id);

  const handleSetCustomBeam = async (e) => {
    e.stopPropagation();
    try {
      // Call the setCustomBeam function which internally calls getBeamKPIs
      const metrics = await setCustomBeam(
        {
          azimuth: localCustomAz,
          elevation: localCustomEl
        },
        props.acqData
      );
      
      // Store the KPI metrics in state
      setKpiMetrics(metrics);
      // Create custom beam data with KPI metrics
      const customBeamData = {
        az: localCustomAz,
        el: localCustomEl,
        ssbarfcn: props.acqData?.['ssb-arfcn'],
        pci: props.acqData.pci,
        pol: props.acqData?.polarization == 'horizontal' ? 'HPOL' : 'VPOL',
        kpiMetrics: metrics
      };
      
      // Update the parent component with the custom beam data
      props.setCustomAzEl({
        az: localCustomAz,
        el: localCustomEl,
        data: customBeamData
      });
      // If onCustomBeamSet callback exists, call it with the custom beam data
      if (props.onCustomBeamSet) {
        props.onCustomBeamSet(customBeamData);
      }
      
      // Store final values for comparison
      setFinalAz(localCustomAz);
      setFinalEl(localCustomEl);
      
      // Dispatch event to notify BeamSelectionForm that custom beam is set
      window.dispatchEvent(new CustomEvent('customBeamSet', { 
        detail: { isSet: true } 
      }));
      
      props.onSuccess?.();
    } catch (error) {
      // Clear custom beam values on error
      setLocalCustomAz('');
      setLocalCustomEl('');
      props.setCustomAzEl({ az: '', el: '' });
    
      // Dispatch event to notify BeamSelectionForm that custom beam failed
      window.dispatchEvent(new CustomEvent('customBeamSet', { 
        detail: { isSet: false } 
      }));
      
      // Set error state to show CommissionErrorBox
      setErrorState({
        title: 'Custom Beam Scan Error',
        message: error.message || 'Failed to set custom beam'
      });
    }
  };

  const handleCancel = (e) => {
    e.stopPropagation();
    cancelCustomBeam();
    
    // Dispatch event to notify BeamSelectionForm that custom beam is cancelled
    window.dispatchEvent(new CustomEvent('customBeamSet', { 
      detail: { isSet: false } 
    }));
    
    // Clear input values
    setLocalCustomAz('');
    setLocalCustomEl('');
    // Clear parent state
    props.setCustomAzEl({ az: '', el: '' });
    props.setAzElInputError({ az: null, el: null });
    // Reset selection
    props.setSelectedBeamRankIndex(0);
    props.grandParent.setState({ selectedBeamRank: 0 });
  };

  const { config } = props;
  let [localCustomAz, setLocalCustomAz] = useState(props.customAzEl?.az);
  let [localCustomEl, setLocalCustomEl] = useState(props.customAzEl?.el);
  let [finalCustomAz, setFinalAz] = useState(props.customAzEl?.az);
  let [finalCustomEl, setFinalEl] = useState(props.customAzEl?.el);

  let [switchInputKey, setSwitchInputKey] = useState(0);

  useEffect(() => {
    setLocalCustomAz(Number.isInteger(props.customAzEl?.az) ? props.customAzEl.az : '');
    setLocalCustomEl(Number.isInteger(props.customAzEl?.el) ? props.customAzEl.el : '');
    props.setAzElInputError &&
      props.setAzElInputError({
        az:
          Number.isInteger(props.customAzEl?.az) && !isAzValid(props.customAzEl?.az, config)
            ? azElErrorMessage.az(config)
            : null,
        el:
          Number.isInteger(props.customAzEl?.el) && !isElValid(props.customAzEl?.el, config)
            ? azElErrorMessage.el(config)
            : null
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, props.customAzEl?.az, props.customAzEl?.el, switchInputKey, props.selectedBeamRankIndex]);

  // When component mounts or unmounts
  useEffect(() => {
    // When error state is cleared, also reset custom beam set status
    return () => {
      if (props.mode === 'custom') {
        window.dispatchEvent(new CustomEvent('customBeamSet', { 
          detail: { isSet: false } 
        }));
      }
    };
  }, [props.mode]);

// In no KPI case manage apply button if no value entered in custom ez/el
  useEffect(() => {
  if (!props.acqData.enableKpis && props.selectedBeamRankIndex === 4) {
    const isCustom = localCustomAz === '' || localCustomEl === '';
    window.dispatchEvent(new CustomEvent('beamRankSelected', { 
      detail: { isCustom } 
    }));
    }
  }, [props.acqData.enableKpis, props.selectedBeamRankIndex, localCustomAz, localCustomEl]);

  // In KPI case manage apply button if no value entered in custom ez/el
  useEffect(() => {
  if (props.acqData.enableKpis && props.selectedBeamRankIndex === 4) {
    if(finalCustomAz !== localCustomAz || finalCustomEl !== localCustomEl ) {
    window.dispatchEvent(new CustomEvent('customBeamSet', { 
      detail: { isSet:  false } 
    }));

    window.dispatchEvent(new CustomEvent('beamRankSelected', { 
      detail: { isCustom:  true } 
    }));
    }
  }
  }, [props.acqData.enableKpis, props.selectedBeamRankIndex, finalCustomAz, finalCustomEl, localCustomAz, localCustomEl]);
  

  if (props.mode === 'custom') {
    return (
      <>
        {errorState && (
          <CommissionErrorBox
            title={errorState.title}
            message={errorState.message}
            onClose={() => {
              setErrorState(null);
              // When error box is closed, ensure custom beam is marked as not set
              window.dispatchEvent(new CustomEvent('customBeamSet', { 
                detail: { isSet: false } 
              }));
            }}
          />
        )}
        <div
          role="option"
          aria-selected={props.index + 1 === props.selectedBeamRankIndex}
          key={props.index}
          className={classNames({
            BeamRankDisplay_BeamRankList_Item_Container: true,
            BeamRankDisplay_BeamRankList_Item_Container_Selected: props.index + 1 === props.selectedBeamRankIndex
          })}
          onClick={() => {
            props.grandParent.setState({ selectedBeamRank: props.index + 1 });
            props.setSelectedBeamRankIndex(props.index + 1);
            
            // Dispatch event to notify BeamSelectionForm that custom beam is selected in kpi case
            {props.acqData.enableKpis && !props.customAzEl.data &&
            window.dispatchEvent(new CustomEvent('beamRankSelected', { 
              detail: { isCustom: true } 
            }));}
          }}
        >
          <div className="BeamRankDisplay_BeamRankList_Item_RankingBox">
            <SvgAF
              customClass={'BeamRankDisplay_BeamRankList_Item_RankingBox_Number'}
              img={
                props.index + 1 === props.selectedBeamRankIndex
                  ? IconLoader.icon_beamrank_c_selected
                  : IconLoader.icon_beamrank_c
              }
            />
            <span className="BeamRankDisplay_BeamRankList_Item_RankingBox_Title">Custom</span>
          </div>
          <div className="BeamRankDisplay_BeamRankList_Item_CustomAzElBox">
            <div className="BeamRankDisplay_BeamRankList_Item_CustomAzElBox_Values">
              <div
                className={classNames({
                  BeamRankDisplay_BeamRankList_Item_CustomAzElBox_Value: true,
                  BeamRankDisplay_BeamRankList_Item_CustomAzElBox_Value_Error:
                    props.azElInputError.az || !isAzValid(localCustomAz, config)
                })}
              >
                <DebounceInput
                  aria-label="Azimuth"
                  onChange={(evt) => {
                    let inputVal = parseInt(evt.target.value);
                    if (!Number.isInteger(inputVal)) {
                      setLocalCustomAz('');
                      props.grandParent.setState({ customAzEl: { az: '', el: props.customAzEl.el } });
                      props.setCustomAzEl({ az: '', el: props.customAzEl.el });
                      return;
                    }
                    setLocalCustomAz(inputVal);
                    if (!isAzValid(inputVal, config)) {
                      let newAzElInputError = {
                        az: azElErrorMessage.az(config),
                        el: props.azElInputError.el
                      };
                      props.setAzElInputError(newAzElInputError);
                    } else {
                      props.setAzElInputError({ az: null, el: props.azElInputError.el });
                      props.grandParent.setState({ customAzEl: { az: inputVal, el: props.customAzEl.el } });
                      props.setCustomAzEl({ az: inputVal, el: props.customAzEl.el });
                    }
                  }}
                  value={localCustomAz}
                  debounceTimeout={TIMEOUT}
                  minLength={0}
                  type={'number'}
                  forceNotifyByEnter={true}
                  forceNotifyOnBlur={true}
                  onClick={(e) => {
                    e.target.select();
                    setSwitchInputKey(Math.random() * 10);
                    props.setAzElInputError({ az: null, el: null });
                  }}
                />
              </div>
              <span>/</span>
              <div
                className={classNames({
                  BeamRankDisplay_BeamRankList_Item_CustomAzElBox_Value: true,
                  BeamRankDisplay_BeamRankList_Item_CustomAzElBox_Value_Error:
                    props.azElInputError.el || !isElValid(localCustomEl, config)
                })}
              >
                <DebounceInput
                  aria-label="Elevation"
                  onChange={(evt) => {
                    let inputVal = parseInt(evt.target.value);
                    if (!Number.isInteger(inputVal)) {
                      setLocalCustomEl('');
                      props.grandParent.setState({ customAzEl: { az: props.customAzEl.az, el: '' } });
                      props.setCustomAzEl({ az: props.customAzEl.az, el: '' });
                      return;
                    }
                    setLocalCustomEl(inputVal);
                    if (!isElValid(inputVal, config)) {
                      let newAzElInputError = {
                        az: props.azElInputError.az,
                        el: azElErrorMessage.el(config)
                      };
                      props.setAzElInputError(newAzElInputError);
                    } else {
                      props.setAzElInputError({ az: props.azElInputError.az, el: null });
                      props.grandParent.setState({ customAzEl: { az: props.customAzEl.az, el: inputVal } });
                      props.setCustomAzEl({ az: props.customAzEl.az, el: inputVal });
                    }
                  }}
                  value={localCustomEl}
                  debounceTimeout={TIMEOUT}
                  minLength={0}
                  forceNotifyByEnter={true}
                  forceNotifyOnBlur={true}
                  type={'number'}
                  onClick={(e) => {
                    e.target.select();
                    setSwitchInputKey(Math.random() * 10);
                    props.setAzElInputError({ az: null, el: null });
                  }}
                />
              </div>
            </div>
            <div className="BeamRankDisplay_BeamRankList_Item_AzElBox_Details">{'(Az/El)'} {props.isAcquisitionWithKpis }</div>
          </div>
        </div>
          {props.acqData.enableKpis && props.index + 1 === props.selectedBeamRankIndex && (
            <div className="BeamRankDisplay_BeamRankList_Item_Actions">
              <div className="BeamRankDisplay_BeamRankList_Item_SetButton_Container">
                <button
                  className="BeamRankDisplay_BeamRankList_Item_SetButton"
                  onClick={handleSetCustomBeam}
                  disabled={
                    localCustomAz === '' || 
                    localCustomEl === '' || 
                    !isAzValid(localCustomAz, config) || 
                    !isElValid(localCustomEl, config) || 
                    isLoading
                  }
                >
                  {isLoading ? 'SETTING...' : 'SET CUSTOM BEAM'}
                </button>
                <div className="BeamRankDisplay_BeamRankList_Item_SetButton_Tooltip">
                  <div>POL: {props.acqData?.polarization === 'horizontal' ? 'HPOL' : props.acqData?.polarization === 'vertical' ? 'VPOL' : 'N/A'}, PCI: {props.acqData?.pci || 'N/A'}, SB-ARFCN: {props.acqData?.['ssb-arfcn'] || 'N/A'}, SCS: {props.acqData?.scs || 'N/A'}</div>
                </div>
              </div>
              <button
                className="BeamRankDisplay_BeamRankList_Item_CancelButton"
                onClick={handleCancel}
                disabled={isLoading}
              >
                CANCEL
              </button>
            </div>
          )}
      </>
    );
  } else {
    return (
      <div
        role="option"
        aria-selected={props.index + 1 === props.selectedBeamRankIndex}
        key={props.index}
        className={classNames({
          BeamRankDisplay_BeamRankList_Item_Container: true,
          BeamRankDisplay_BeamRankList_Item_Container_Selected: props.index + 1 === props.selectedBeamRankIndex
        })}
        onClick={() => {
          props.grandParent.setState({ selectedBeamRank: props.index + 1 });
          props.setSelectedBeamRankIndex(props.index + 1);
          
          // Dispatch event to notify BeamSelectionForm that a regular beam is selected
          window.dispatchEvent(new CustomEvent('beamRankSelected', { 
            detail: { isCustom: false } 
          }));
        }}
      >
        <div className="BeamRankDisplay_BeamRankList_Item_RankingBox">
          <SvgAF
            customClass={'BeamRankDisplay_BeamRankList_Item_RankingBox_Number'}
            img={
              IconLoader[
                props.index + 1 === props.selectedBeamRankIndex
                  ? `icon_beamrank_${props.index + 1}_selected`
                  : `icon_beamrank_${props.index + 1}`
              ]
            }
          />
          <span className="BeamRankDisplay_BeamRankList_Item_RankingBox_Title">Rank {props.index + 1}</span>
        </div>
        <div className="BeamRankDisplay_BeamRankList_Item_AzElBox">
          <div className="BeamRankDisplay_BeamRankList_Item_AzElBox_Values">
            <div className="BeamRankDisplay_BeamRankList_Item_AzElBox_Value">{props.entry?.az}</div>
            <span>/</span>
            <div className="BeamRankDisplay_BeamRankList_Item_AzElBox_Value">{props.entry?.el}</div>
          </div>
          <div className="BeamRankDisplay_BeamRankList_Item_AzElBox_Details">{'(Az/El)'}</div>
        </div>
      </div>
    );
  }
};

export default BeamRankListItem;
